import { useRef, useLayoutEffect } from "react";
import type { ReactNode } from "react";

import { useOverlayContext } from "@shared/context/OverlayContext";

import { DropdownDivider } from "../DropdownDivider";

import * as styles from "./DropdownHeader.styles";

interface Props {
  /**
   * Sets implementation of boilerplate styles
   *
   * @default false
   */
  noStyle?: boolean;
  /**
   * Allows for increased padding version
   *
   * @default false
   */
  isLargePadding?: boolean;
  /**
   * sets inclusion of divider between header and content
   *
   * @default: true
   */
  showDivider?: boolean;
  /**
   * The header content, most likely just some text
   *
   * @default undefined
   */
  children?: ReactNode;
}

/**
 * @deprecated Use DropdownMenu.Group with the `label` prop from `@fullscript/aviary-web` instead.
 *
 * @example
 * ```tsx
 * <DropdownMenu.Group label="Header">
 *   <DropdownMenu.Item>Item 1</DropdownMenu.Item>
 *   <DropdownMenu.Item>Item 2</DropdownMenu.Item>
 * </DropdownMenu.Group>
 * ```
 */
const DropdownHeader = ({
  children,
  noStyle = false,
  isLargePadding,
  showDivider = true,
  ...rest
}: Props) => {
  const conditionalStyles = [!noStyle && styles.dropdownHeader];
  const contentStyles = [
    !noStyle && styles.headerContent,
    isLargePadding && styles.largePadding,
    showDivider && styles.dividerShims,
  ];
  const overlayContext = useOverlayContext();
  const headerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!overlayContext || !headerRef.current) return;

    overlayContext.registerTitleElement(headerRef.current);

    return () => overlayContext.registerTitleElement(null);
  }, [overlayContext]);

  return (
    <div css={conditionalStyles} ref={headerRef} {...rest}>
      <div css={contentStyles}>{children}</div>
      {!!showDivider && <DropdownDivider hasTopMargin={false} />}
    </div>
  );
};

export { DropdownHeader };
